import React, { Component } from 'react';
import moment from 'moment';
import Axios from 'axios';
import firebase from 'firebase/app';
import 'firebase/database';
import { Bar } from 'react-chartjs-2';
import { isEmpty, isEqual, isObject, chunk, uniq, get } from 'lodash';
import Loading from '../../../components/Loading';
import { getDateArray } from '../../../helpers/get_date_array';
import { filterOutSoundcastBundles } from '../../../helpers/soundcasts';
import { getSoundcastsManaged, canUserAccessFeature } from '../../../helpers/userinfoHelper';
import Colors from '../../../styles/colors';
import { NoAccess, BaseButton, RowDivision, LoadingWrapper } from '../../../styles/styled';
import { CSVLink } from 'react-csv';
import { ALERTS } from '../../../helpers/texts';

const printableValue = inputValue => {
  // const outValue = Math.floor(inputValue);
  // const outValue = inputValue < 0.1 
  //   ? (inputValue < 0.01 ? 0.01 : inputValue.toFixed(2)) 
  //   : inputValue.toFixed(1);
  const outValue = inputValue < 0.01 ? 0.01 : inputValue.toFixed(2)
  return outValue;
};

const prettyTrafficValue = (inputValue, unit = '') => {
  if (isEmpty(unit)) {
    return inputValue > 750000000 ? `${(inputValue / (1024 * 1024 * 1024)).toFixed(2)} GB`
    : inputValue > 750000 ? `${(inputValue / (1024 * 1024)).toFixed(2)} MB`
    : inputValue > 9999 ? `${(inputValue / 1024).toFixed(2)} kB`
    : `${inputValue}`;
  }
  return `${inputValue.toFixed(2)} ${unit}`.trimEnd(); 
};

const secondsToHms = (seconds, isSecondsVisible) => {
  if (!seconds) return '';

  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds / 60) % 60;
  const s = parseInt(seconds, 10) % 60;

  const hDisplay = h > 0 ? `${h}h ` : '';
  const mDisplay = m > 0 ? ` ${m}m ` : '';
  const sDisplay = s > 0 && !isSecondsVisible ? '' : ` ${s}s`;

  return hDisplay + mDisplay + sDisplay;
};
const minutesToHms = minutes => secondsToHms(Math.floor(minutes * 60), true);

const dataGridStyle = {
  backgroundColor: 'rgba(97, 225, 251, 0.2)',
  borderColor: 'rgba(97, 225, 251, 1)',
  borderWidth: 1,
  hoverBackgroundColor: 'rgba(97, 225, 251, 0.4)',
  hoverBorderColor: 'rgba(97, 225, 251, 1)',
};

const barOptions = (cb = value => { return value; }, options = {}) => {
  const { yMax = 10, yTitle = '' } = options || {};
  return {
    maintainAspectRatio: false,
    scales: {
      xAxes: [{ gridLines: { display: false } }],
      yAxes: [{ ticks: { beginAtZero: true, min: 0, suggestedMax: yMax },
        scaleLabel: {
          display: !isEmpty(yTitle),
          labelString: yTitle,
        },
      }],
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data['datasets'][0];
          const unit = dataset.unit || '';
          const value = dataset['data'][tooltipItem['index']];
          const newValue = cb(value, unit);
          return `${dataset.label}: ${newValue}`;
        },
      },
    },
  };
};

const csvHeaders = [
  { label: 'Soundcast', key: 'soundcast' },
  { label: 'Start date', key: 'startDate' },
  { label: 'End Date', key: 'endDate' },
  { label: 'First Name', key: 'firstName' },
  { label: 'Last Name', key: 'lastName' },
  { label: 'Email', key: 'email' },
  { label: 'Total minutes listened', key: 'totalMinutes' },
];

const emptyOnSoundwiseTab = {
  data: {
    labels: [],
    datasets: [{ label: 'Total minutes', ...dataGridStyle, data: [] }], // onsoundwise tab data
  },
  activeListeners: [],
  totalListens: 0,
  userArr: [],
  episodeArr: [],
};

const emptyOverallTab = {
  overallData: {
    labels: [],
    datasets: [{ label: 'Total # of downloads', ...dataGridStyle, data: [] }],
  },
  overallEpisodes: [],
  countries: [],
  cities: [],
  agents: [],
  browsers: [],
};

const cloudfrontDatasetLabel = 'Data volume of downloads';
const emptyCloudfrontTab = {
  cfData: {
    labels: [],
    datasets: [{ label: cloudfrontDatasetLabel, ...dataGridStyle, data: [] }],
  },
  cfDataUnit: '',
  cfTotalTraffic: 0,
  cfEpisodes: [],
};

const bandwidthDatasetLabel = 'Total bandwidth of downloads';
const emptyTotalBandwidthTab = {
  bandwidthData: {
    labels: [],
    datasets: [{ label: bandwidthDatasetLabel, ...dataGridStyle, data: [] }],
  },
  bandwidthDataUnit: '',
  bandwidthTotalBytes: 0,
  bandwidthTable: [],
};

const daysIntervalDefault =  7;   // days interval selectbox, default 7 days
const monthsIntervalDefault = 6;  // months interval selectbox, default 6 months

export default class Analytics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // On Soundwise tab
      ...emptyOnSoundwiseTab,
      // Overall tab
      ...emptyOverallTab,
      // Cloudfront tab
      ...emptyCloudfrontTab,
      // Total bandwidth tab
      ...emptyTotalBandwidthTab,

      isLoading: true,
      isLoadingTopTracks: true, // On Soundwise tab
      isLoadingTopListeners: true, // On Soundwise tab
      loadingPercent: '', // On Soundwise tab
      lastError: '',
      publisherID: null,
      currentSoundcastID: null,
      currentSoundcast: null,
      soundcasts_managed: [],
      allowed: false,
      activeTab: 'onsoundwise', // valid values: 'onsoundwise', 'overall', 'cloudfrontstat', 'bandwidth'
      daysInterval: daysIntervalDefault,
      monthsInterval: monthsIntervalDefault,
      disabledYearInterval:  moment().year() < 2024,
    };

    this.loadData = this.loadData.bind(this);
    this.changeSoundcastId = this.changeSoundcastId.bind(this);
    this.activateTab = this.activateTab.bind(this);
    this.changeDaysInterval = this.changeDaysInterval.bind(this);
    this.changeMonthsInterval = this.changeMonthsInterval.bind(this);
    this.getListeningStats = this.getListeningStats.bind(this);
    this.aggregateListenings = this.aggregateListenings.bind(this);
    this.compileEpisodeList = this.compileEpisodeList.bind(this);
    this.compileUserList = this.compileUserList.bind(this);
    this.getOveralTabData = this.getOveralTabData.bind(this);
    this.getCloudfrontTabData = this.getCloudfrontTabData.bind(this);
    this.getTotalBandwidthTabData = this.getTotalBandwidthTabData.bind(this);
    this.isTotal = this.isTotal.bind(this);
    this.getSoundcastsTitles = this.getSoundcastsTitles.bind(this);
  }

  componentDidMount() {
    const { userInfo } = this.props;
    const { publisherID = '' } = userInfo || {};
    this.setState(
      {
        publisherID,
        allowed: canUserAccessFeature('VIEW_ANALYTICS'),
      },
      () => {
        if (
          userInfo.soundcasts_managed &&
          typeof Object.values(userInfo.soundcasts_managed)[0] == 'object'
        ) {
          this.loadData(userInfo);
        } else {
          setTimeout(() => this.setState({ isLoading: false }), 2000);
        }
      }
    );
  }

  componentWillReceiveProps(nextProps) {
    const { userInfo } = nextProps;
    const { publisherID = '' } = userInfo || {};
    this.setState(
      {
        publisherID,
        allowed: canUserAccessFeature('VIEW_ANALYTICS'),
      },
      () => {
        if (nextProps.userInfo.soundcasts_managed) {
          if (typeof Object.values(nextProps.userInfo.soundcasts_managed)[0] == 'object') {
            this.loadData(nextProps.userInfo);
          }
        }
      }
    );
  }

  loadData(userInfo) {
    if (this.dataLoadedLock) {
      return; // prevent double loading on direct analytics page refresh
    }
    this.dataLoadedLock = true;

    const soundcasts_managed = getSoundcastsManaged(userInfo);
    if (canUserAccessFeature('TOTAL_ANALYTICS')) {
      soundcasts_managed.push({
        id: 'total',
        title: 'Total',
      });
    }

    const currentSoundcastID = get(this.props, 'history.location.state.soundcastId', 
      isEmpty(soundcasts_managed) ? null : soundcasts_managed[0].id
    );
    const currentSoundcast = soundcasts_managed
      .find(item => item.soundcastId === currentSoundcastID) || null;

    this.setState({
      soundcasts_managed,
      currentSoundcastID,
      currentSoundcast,
    }, () => this.getListeningStats(currentSoundcastID));
  }

  isTotal(soundcastId) {
    return soundcastId === 'total';
  }

  changeSoundcastId(e) {
    const currentSoundcastID = e.target.value;
    this.setState({ currentSoundcastID }, () => {
      const { soundcasts_managed, activeTab: currentActiveTab } = this.state;
      this.setState({
        currentSoundcast: soundcasts_managed.find(soundcast => soundcast.id === currentSoundcastID),
      }, () => {
        if (this.isTotal(currentSoundcastID) && currentActiveTab === 'overall') {
          this.activateTab('onsoundwise');
        } else {
          const defaultMode = this.isTotal(currentSoundcastID) && currentActiveTab === 'onsoundwise';
          this.activateTab(currentActiveTab, defaultMode);
        }
      });
    });
  }

  activateTab(activeTab, defaultMode = true) {
    this.setState({
      activeTab,
      ...(defaultMode ? { daysInterval: daysIntervalDefault, monthsInterval: monthsIntervalDefault } : {}),
    }, () => {
      if (activeTab === 'onsoundwise') {
        this.getListeningStats();
      } else if (activeTab === 'overall') {
        this.getOveralTabData();
      } else if (activeTab === 'cloudfrontstat') {
        this.getCloudfrontTabData();
      } else if (activeTab === 'bandwidth') {
        this.getTotalBandwidthTabData();
      }
    });
  }

  changeDaysInterval(daysInterval) {
    this.setState({ daysInterval }, () => {
      if (this.state.activeTab === 'onsoundwise') {
        this.getListeningStats();
      } else if (this.state.activeTab === 'overall') {
        this.getOveralTabData();
      } else if (this.state.activeTab === 'cloudfrontstat') {
        this.getCloudfrontTabData();
      }
    });
  }

  changeMonthsInterval(monthsInterval) {
    this.setState({ monthsInterval }, () => {
      this.getTotalBandwidthTabData();
    });
  }

  getListeningStats(soundcastId) {
    const { daysInterval, currentSoundcastID, publisherID } = this.state;
    if (isEmpty(soundcastId)) {
      soundcastId = currentSoundcastID;
    }

    const endDate = moment().format('YYYY-MM-DD');
    const end = moment(endDate);
    const start = moment().subtract(daysInterval, 'days');
    const startDate = start.format('YYYY-MM-DD');
    const labels = [];
    for (let date = moment(startDate); date.isSameOrBefore(end); date.add(1, 'days')) {
      labels.push(date.format('YYYY-MM-DD'));
    }
    const url = this.isTotal(soundcastId)
      ? `/api/v2/analytics/publisher/${publisherID}`
      : `/api/v2/analytics/soundcast/${soundcastId}`;
    let data = {
      activeListeners: [],
      totalSec: 0,
      statsByDate: {},
      statsByEpisode: {},
      statsByUser: {},
    };
    const emptyOnSoundwiseTabState = (loading = false) => {
      return {
        ...emptyOnSoundwiseTab,
        data: { labels, datasets: emptyOnSoundwiseTab.datasets },
        isLoading: loading,
        isLoadingTopTracks: loading,
        isLoadingTopListeners: loading,
        lastError: '',
        loadingPercent: '',
      };
    };

    this.setState({
      ...emptyOnSoundwiseTabState(true),
      loadingPercent: `0%`,
    }, async () => {
      await new Promise(resolve => setTimeout(resolve, 850));
      try {
        // const res = await Axios.get(url, { params: { startDate, endDate } });
        // const { data = {} } = res || {};

        const calcMaxGroupLength = days => days > 60 ? 4 : days > 30 ? 3 : days > 7 ? 2 : 1;
        const maxGroupLength = calcMaxGroupLength(daysInterval); // max 4
        
        const labelGroups = chunk(labels, maxGroupLength);
        const tmpPP = Math.ceil((labelGroups.length) / 5);
        const maxParallelPromises = tmpPP > 8 ? 8 : tmpPP || 1; // max 8

        const promises = labelGroups.map(dates => {
          return new Promise(async (resolve, reject) => {
            try {
              const res = await Axios.get(url, {
                params: { startDate: dates[0], endDate: dates[dates.length - 1] }
              });
              const { data = {} } = res || {};
              resolve(data);
            } catch (err) {
              reject(err);
            }
          });
        });

        const processGroupsRecursive = (groups, groupNum = 0) => {
          return new Promise(async (resolve, reject) => {
            try {
              const results = await Promise.all(groups[groupNum]);
              for (const result of results) {
                data = this.aggregateListenings(data, result);
              }
              const { activeListeners = [], totalSec = 0, statsByDate = {} } = data;
              const statsByDate_length = labels.map(date => {
                return date in statsByDate ? statsByDate[date].length / 60 : 0;
              });
              const datasets = [{ label: 'Total minutes', ...dataGridStyle, data: statsByDate_length }];

              const isLatestGroup = groupNum + 1 >= groups.length;
              let latestState = {};
              if (isLatestGroup) {
                const { statsByEpisode = {}, statsByUser = {} } = data || {};
                // await Promise.all([
                  this.compileEpisodeList(statsByEpisode),
                  this.compileUserList(statsByUser),
                // ]);
                latestState = {
                  isLoading: false,
                  loadingPercent: '',
                  lastError: '',
                };
              }
              this.setState({
                data: { labels, datasets },
                totalListens: secondsToHms(totalSec, true) || 0,
                activeListeners,
                loadingPercent: `${((groupNum+1)/groups.length*100).toFixed(0)}%`,
                ...latestState,
              }, async () => {
                if (isLatestGroup) {
                  return resolve();
                }
                await processGroupsRecursive( groups, groupNum + 1);
              });
            } catch (err) {
              reject(err);
            }
          })
        };

        const groups = chunk(promises, maxParallelPromises);
        await processGroupsRecursive(groups);

      } catch (err) {
        let lastError = 'ERROR WHILE RECEIVING DATA';
        const errData = err && err.response && err.response.data;

        let errMessage = ''
        if(!isEmpty(errData)){
          const { success = false, statusCode = 0, message = '' } = errData;
          if (statusCode === 409) {
            lastError = 'TOO MUCH DATA TO ANALYZE';
          }
          errMessage = message;
        }

        this.setState({
          ...emptyOnSoundwiseTabState(false),
          lastError,
        }, () => {
          const errPrefix = `Error: Analytics getListeningStats ${
            this.isTotal(soundcastId) ? publisherID : soundcastId
          } ${start} ${end}`;
          console.error(`${errPrefix} ${err && err.toString()}`, errData);

          if (!isEmpty(errMessage)) {
            alert(errMessage);
          } else {
            alert(`${errPrefix} ${err && err.toString()} ${JSON.stringify(errData)}`);
          }
        });
      }
    });
  }

  aggregateListenings(dataObj1 = {}, dataObj2 = {}) {
    const sumItems = (item1, item2) => {
      return {
        listens: item1.listens + item2.listens,
        length:  item1.length  + item2.length,
      }
    };
    const sumObjs = (obj1, obj2) => {
      const obj = { ...obj1 };
      for (const key in obj2) {
        if (key in obj) {
          obj[key] = sumItems(obj1[key], obj2[key]);
        } else {
          obj[key] = { ...obj2[key] }
        }
      }
      return obj;
    };
    const dataObj = {};
    dataObj.totalSec = dataObj1.totalSec + dataObj2.totalSec;
    dataObj.activeListeners = uniq([ ...(dataObj1.activeListeners || []), ...(dataObj2.activeListeners || []) ]);
    dataObj.statsByDate = sumObjs(dataObj1.statsByDate, dataObj2.statsByDate);
    dataObj.statsByEpisode = sumObjs(dataObj1.statsByEpisode, dataObj2.statsByEpisode);
    dataObj.statsByUser = sumObjs(dataObj1.statsByUser, dataObj2.statsByUser);
    return dataObj;
  }

  getSoundcastsTitles() {
    const { soundcasts_managed: soundcasts } = this.state;

    const resultArray = [];
    soundcasts.forEach(soundcast => {
      const { id, title } = soundcast;
      if (!isEmpty(id)) {
        resultArray.push({
          [id]: title,
        });
      }
    });

    return resultArray.reduce((items, item) => ({ ...items, ...item }), {});
  }

  async compileEpisodeList(statsByEpisode) {
    const statsByEpisodeArr = []; // top episodes
    const keysEpisode = Object.keys(statsByEpisode);
    for (const episodeId of keysEpisode) {
      const { listens, length } = statsByEpisode[episodeId];
      statsByEpisodeArr.push({ episodeId, listens, length });
    }
    statsByEpisodeArr.sort((a, b) => b.listens - a.listens);

    const soundcastsTitles = this.getSoundcastsTitles();
    const episodeArr = [];
    const promises = statsByEpisodeArr.map((episode, i) => {
      const { episodeId } = episode;
      return firebase
        .database()
        .ref(`episodes/${episodeId}`)
        .once('value')
        .then(snapshot => {
          const episodeValue = snapshot.val() || {};
          const { title, date_created, soundcastID } = episodeValue;
          if (!title || !date_created) {
            return Promise.resolve(); // skip empty
          }
          const dateFixed = new Date(1000 * date_created).toISOString().slice(0, 10);
          const soundcastsTitle = soundcastsTitles[soundcastID] || '';
          episodeArr.push({ ...episode, title, date_created: dateFixed, soundcastsTitle });
        })
        .then(
          res => res,
          err => console.error(`Error: compileEpisodeList ${episodeId}`, err)
        );
    });

    await Promise.all(promises).then(
      res => {
        if (episodeArr.length > 20) {
          // setting "Other" label
          const otherLabelItem = { title: 'Other', date_created: '-', listens: 0, length: 0 };
          while (episodeArr.length >= 20) {
            const item = episodeArr.pop();
            otherLabelItem.listens += item.listens;
            otherLabelItem.length += item.length;
          }
          episodeArr.push(otherLabelItem);
        }
        this.setState({
          episodeArr,
          isLoadingTopTracks: false,
        });
      },
      err => console.log('Error: compileEpisodeList promise.all', err)
    );
  }

  async compileUserList(statsByUser) {
    const { userInfo } = this.props;
    const statsByUserArr = []; // top subscribers
    const keysUser = Object.keys(statsByUser);
    for (const userId of keysUser) {
      if (userId !== userInfo.id) {
        const { listens, length } = statsByUser[userId];
        statsByUserArr.push({ userId, listens, length });
      }
    }
    statsByUserArr.sort((a, b) => b.length - a.length);

    const userArr = [];
    const csvData = [];
    const promises = statsByUserArr.map((user, i) => {
      return firebase
        .database()
        .ref(`users/${user.userId}`)
        .once('value')
        .then(snapshot => {
          if (!snapshot.exists()) {
            return;
          }
          const userData = snapshot.val() || {};
          if (isEmpty(userData)) {
            return;
          }
          const { firstName, lastName, pic_url: picUrl, email = [''] } = userData;
          let csvObject = {
            soundcast: '',
            period: '',
            firstName,
            lastName,
            email: email[0],
            totalMinutes: printableValue(user.length / 60),
          };

          if (i === 0) {
            csvObject.soundcast = this.state.currentSoundcast
              ? this.state.currentSoundcast.title
              : '';
            const selectedPeriod = this.state.daysInterval ? this.state.daysInterval : 0;
            const startDate = moment().subtract(selectedPeriod, 'days').format('MM/DD/YYYY');
            const endDate = moment().format('MM/DD/YYYY');

            csvObject.startDate = startDate ? startDate : '';
            csvObject.endDate = endDate ? endDate : '';
          }

          const picUrlFixed =
            isEmpty(picUrl) ||
            picUrl === 'https://d1jzcuf08rvzm.cloudfront.net/user_profile_pic_placeholder.png'
              ? 'https://soundwiseinc.s3.amazonaws.com/user_profile_pic_placeholder.png'
              : picUrl;

          csvData.push(csvObject);
          userArr.push({ ...user, firstName, lastName, pic_url: picUrlFixed });
        })
        .then(
          res => res,
          err => console.error(`Error: compileUserList ${user.userId}`, err)
        );
    });

    await Promise.all(promises).then(
      res => {
        if (userArr.length > 20) {
          // setting "Other" label
          const otherLabelItem = { firstName: 'Other', listens: 0, length: 0 };
          while (userArr.length >= 20) {
            const item = userArr.pop();
            otherLabelItem.listens += item.listens;
            otherLabelItem.length += item.length;
          }
          userArr.push(otherLabelItem);
        }
        this.setState({
          userArr,
          csvData,
          isLoadingTopListeners: false,
        });
      },
      err => console.log('Error: compileUserList promise.all', err)
    );
  }

  getOveralTabData() {
    this.setState({ isLoading: true, lastError: '' }, () => {
      // setTimeout(() => this.setState({ isLoading: false }), 10000); // max 10 sec <-- ???
      const { daysInterval, currentSoundcastID } = this.state;
      // this.isTotal(currentSoundcastID) // TODO
      const errPrefix = `Error: Analytics getOveralTabData ${currentSoundcastID} ${daysInterval}`;
      const soundcast = `soundcastId=${currentSoundcastID}`;
      Axios.get(`/api/trackrequests/loadAnalytics?${soundcast}&interval=${daysInterval}`)
        .then(result => {
          const res = result && result.data && result.data.res;
          if (!res || res.error) {
            return alert(`${errPrefix} ${(res && res.error) || ''}`);
          }
          // load overall tab graph data and tables
          const { data, overallEpisodes, countries, cities, agents, browsers } = res;
          const startDate = moment().subtract(daysInterval, 'days');
          const endDate = moment().format();
          const overallData = {
            labels: getDateArray(startDate.format().slice(0, 10), endDate.slice(0, 10), 1),
            datasets: [{ label: 'Total # of downloads', ...dataGridStyle, data }],
          };
          this.setState({
            isLoading: false,
            lastError: '',
            overallData,
            overallEpisodes,
            countries,
            cities,
            agents,
            browsers,
          });
        })
        .catch(err => {
          const lastError = 'ERROR WHILE RECEIVING DATA';
          this.setState({ ...emptyOverallTab, isLoading: false, lastError }, () => {
            const { response = {} } = err;
            const { status, data: rdata = '' } = response;
            let errData = !isEmpty(rdata) ? rdata : err.toString();
            if (status && status >= 500) {
              errData =
                'There was a problem reading overall data, please contact support at support@mysoundwise.com if the issue persists.';
            }

            alert(errData);
            console.error(errPrefix, errData);
          });
        });
    });
  }

  getCloudfrontTabData() {
    this.setState({ isLoading: true, lastError: '' }, () => {
      const { daysInterval, currentSoundcastID, publisherID } = this.state;
      const errPrefix = `Error: Analytics getCloudfrontTabData ${currentSoundcastID} ${daysInterval}`;
      const startDate = moment().subtract(daysInterval, 'days').format('YYYY-MM-DD');
      const endDate = moment().format('YYYY-MM-DD');
      const dateQuery = `startDate=${startDate}&endDate=${endDate}`;
      const csUrl = 
        isEmpty(publisherID) && isEmpty(currentSoundcastID) ? '' : this.isTotal(currentSoundcastID)
        ? `/api/analytics/cloudfrontstats/publishers/${publisherID}?${dateQuery}`
        : `/api/analytics/cloudfrontstats/soundcasts/${currentSoundcastID}?${dateQuery}`;

      if (isEmpty(csUrl)) {
        return this.setState({...emptyCloudfrontTab, isLoading: false, lastError: '' });
      }
      Axios.get(csUrl)
        .then(result => {
          const { data: { success = false, data = {}, message: lastError = '' } } = result || {};
          if (!success) {
            this.setState({ ...emptyCloudfrontTab, isLoading: false, lastError }, () => {
              const errorMessage = `${errPrefix} ${lastError || 'UNKNOWN ERROR'}`;
              console.error(errorMessage);
              return alert(errorMessage);
            });
          }
          // load cloudfront tab graph data and tables
          const {
            cfData: resCfData = {},
            cfEpisodes = [],
            cfTotalTraffic = 0, // by resCfData.unit
            cfTotalTrafficBytes = 0, // bytes
          } = data || {};
          const cfDataUnit = resCfData.unit || '';
          const { labels = [], datasetData = {} } = resCfData || {};
          const cfData = isEmpty(datasetData)
            ? emptyCloudfrontTab.cfData 
            : {
              labels,
              datasets: [{
                label: cloudfrontDatasetLabel,
                data: datasetData,
                unit: cfDataUnit,
                ...dataGridStyle,
              }],
            };

          this.setState({
            isLoading: false,
            lastError: '',
            cfData,
            cfDataUnit,
            cfEpisodes,
            cfTotalTraffic: cfTotalTrafficBytes,
          });
        })
        .catch(err => {
          const lastError = 'ERROR WHILE RECEIVING DATA';
          this.setState({ ...emptyCloudfrontTab, isLoading: false, lastError }, () => {
            const { response = {} } = err;
            const { status, data: rdata = '' } = response;
            let errMessage = isEmpty(rdata) ? err.toString()
              : isObject(rdata) ? `ERROR: ${rdata.message || err.toString()}`
              : rdata;
            if (status && status >= 500) {
              errMessage =
                'There was a problem reading overall data, please contact support at support@mysoundwise.com if the issue persists.';
            }
            alert(errMessage);
            console.error(errPrefix, errMessage);
          });
        });
    });
  }

  getTotalBandwidthTabData() {
    this.setState({ isLoading: true, lastError: '' }, () => {
      const { monthsInterval = 6, publisherID } = this.state;
      const errPrefix = `Error: Analytics Total Bandwidth Tab ${monthsInterval}`;
      const csUrl = `/api/analytics/publisher/${publisherID}/totaltraffic?months=${monthsInterval}`;
      Axios.get(csUrl)
        .then(result => {
          const { data: { success = false, data = {}, message: lastError = '' } } = result || {};
          if (!success) {
            this.setState({ ...emptyTotalBandwidthTab, isLoading: false, lastError }, () => {
              const errorMessage = `${errPrefix} ${lastError || 'UNKNOWN ERROR'}`;
              console.error(errorMessage);
              return alert(errorMessage);
            });
          }
          // load Total Bandwidth tab graph data and tables
          const {
            bandwidthData: resBandwidthData = {},
            bandwidthTable = [],
            bandwidthTotalBytes = 0,
          } = data || {};
          const bandwidthDataUnit = resBandwidthData.unit || '';

          const { labels = [], datasetData = {} } = resBandwidthData || {};
          const bandwidthData = isEmpty(datasetData)
            ? emptyTotalBandwidthTab.bandwidthData 
            : {
              labels,
              datasets: [{
                label: bandwidthDatasetLabel,
                data: datasetData,
                unit: bandwidthDataUnit,
                ...dataGridStyle,
              }],
            };

          this.setState({
            isLoading: false,
            lastError: '',
            bandwidthData,
            bandwidthDataUnit,
            bandwidthTotalBytes,
            bandwidthTable,
          });
        })
        .catch(err => {
          const lastError = 'ERROR WHILE RECEIVING DATA';
          this.setState({ ...emptyTotalBandwidthTab, isLoading: false, lastError }, () => {
            const { response = {} } = err;
            const { status, data: rdata = '' } = response;
            let errMessage = isEmpty(rdata) ? err.toString()
              : isObject(rdata) ? `ERROR: ${rdata.message || err.toString()}`
              : rdata;
            if (status && status >= 500) {
              errMessage =
                'There was a problem reading overall data, please contact support at support@mysoundwise.com if the issue persists.';
            }
            alert(errMessage);
            console.error(errPrefix, errMessage);
          });
        });
    });
  }

  render() {
    const {
      soundcasts_managed,
      currentSoundcastID,
      activeTab,
      allowed,
      isLoading = false,
      isLoadingTopTracks = false,
      isLoadingTopListeners = false,
      loadingPercent,
      lastError,
    } = this.state;
    const {
      data,
      episodeArr,
      userArr,
      totalListens,
      activeListeners,
      csvData,
      daysInterval,
      monthsInterval,
      currentSoundcast,
    } = this.state; // "onsoundwise" tab data
    const {
      overallData,
      overallEpisodes,
      countries,
      cities,
      agents,
      browsers,
    } = this.state; // "overall" tab data
    const {
      cfData,
      cfDataUnit,
      cfEpisodes,
      cfTotalTraffic,
    } = this.state; // "cloudfrontstat" tab data
    const {
      bandwidthData,
      bandwidthDataUnit,
      bandwidthTotalBytes,
      bandwidthTable,
      disabledYearInterval,
    } = this.state; // "bandwidth" tab data

    const { history } = this.props;

    if (isEmpty(soundcasts_managed)) {
      return (
        <RowDivision className="row">
          <LoadingWrapper height="90">
            <Loading text="Verifying user, please wait" />
          </LoadingWrapper>
        </RowDivision>
      );
    }

    if (!allowed) {
      return (
        <NoAccess>
          <div className="upgrade padding-five sm-padding-ten sm-width-90 xs-width-100 bg-white">
            <div className="title-medium sm-title-medium xs-title-medium font-weight-600 margin-five-bottom">
              Upgrade to view analytics
            </div>
            <div className="text-large sm-text-large line-height-26 margin-five-bottom">
              {ALERTS().VIEW_ANALYTICS.NO_ACCESS}
            </div>
            <BaseButton onClick={() => history.push('/pricing')}>UPGRADE</BaseButton>
          </div>
        </NoAccess>
      );
    }

    const daysIntervals = [
      [7, `7 days`],
      [30, `30 days`],
      [60, `60 days`],
      [365, `year`], // ? onsoundwise & total
    ];
    // if (activeTab !== 'onsoundwise' || !this.isTotal(currentSoundcastID)) {
    //   daysIntervals.push([365, `year`]);
    // }

    const monthsIntervals = [
      [3, `3 months`],
      [6, `6 months`],
      [9, `9 months`],
      [12, `year`],
    ];

    return (
      <div>
        <div>
          <div className="padding-bottom-20px padding-top-20px">
            <span className="title-medium ">Analytics</span>
            <div style={{
              ...styles.soundcastSelectWrapper,
              visibility: activeTab !== 'bandwidth' ? 'visible' : 'hidden',
            }}>
              <select
                style={styles.soundcastSelect}
                onChange={e => this.changeSoundcastId(e)}
                value={currentSoundcastID || ''}>
                {filterOutSoundcastBundles(soundcasts_managed).map((souncast, i) => {
                  return (
                    <option style={styles.option} value={souncast.id} key={i}>
                      {souncast.title}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            <span
              style={{
                color: activeTab === 'onsoundwise' ? Colors.mainOrange : '',
                cursor: activeTab === 'onsoundwise' ? '' : 'pointer',
              }}
              onClick={e => this.activateTab('onsoundwise')}>
              On Soundwise
            </span>
            <span
              style={{
                visibility: this.isTotal(currentSoundcastID) ? 'hidden' : 'visible',
                color: activeTab === 'overall' ? Colors.mainOrange : '',
                cursor: activeTab === 'overall' ? '' : 'pointer',
                marginLeft: 9,
              }}
              onClick={e => this.activateTab('overall')}>
              Overall
            </span>
            <span
              style={{
                color: activeTab === 'cloudfrontstat' ? Colors.mainOrange : '',
                cursor: activeTab === 'cloudfrontstat' ? '' : 'pointer',
                marginLeft: 9,
              }}
              onClick={e => this.activateTab('cloudfrontstat')}>
              Data volume
            </span>
            <span
              style={{
                color: activeTab === 'bandwidth' ? Colors.mainOrange : '',
                cursor: activeTab === 'bandwidth' ? '' : 'pointer',
                marginLeft: 9,
              }}
              onClick={e => this.activateTab('bandwidth')}>
              Total bandwidth
            </span>
          </div>

          <div style={{ ...styles.colWrapper }}>
            <div style={styles.selectWrapper}>
              <span style={{ paddingRight: 10 }}>
                Total {activeTab === 'overall' ? 'downloads'
                  : activeTab === 'onsoundwise' ? 'listens'
                  : activeTab === 'cloudfrontstat' ? 'traffic'
                  : activeTab === 'bandwidth' ? 'traffic'
                  : ''
                }
              </span>
              <select
                style={{ 
                  ...styles.soundcastSelect,
                  ...styles.intervalSelect,
                  display: activeTab !== 'bandwidth' ? 'inline-block' : 'none',
                }}
                value={daysInterval}
                onChange={e => this.changeDaysInterval(e.target.value)}>
                {daysIntervals.map((interval, index) => (
                  <option style={{ fontSize: 14 }} value={`${interval[0]}`} key={index}>
                    {`Last ${interval[1]}`}
                  </option>
                ))}
              </select>
              <select
                style={{ 
                  ...styles.soundcastSelect,
                  ...styles.intervalSelect,
                  display: activeTab === 'bandwidth' ? 'inline-block' : 'none',
                }}
                value={monthsInterval}
                onChange={e => this.changeMonthsInterval(e.target.value)}>
                {monthsIntervals.map((interval, index) => (
                  <option
                    disabled={interval[0] === 12 && disabledYearInterval}
                    style={{ fontSize: 14 }} 
                    value={`${interval[0]}`}
                    key={index}>
                    {`Last ${interval[1]}${interval[0] === 12 && disabledYearInterval
                      ? ' (disabled until 2024)' : ''}`}
                  </option>
                ))}
              </select>
              <span
                style={{
                  paddingRight: 10,
                  display:
                    activeTab === 'onsoundwise' && !isLoading && isEmpty(lastError)
                      ? 'inline-block'
                      : 'none',
                }}>
                {`Total listens: ${totalListens}`}
              </span>
              <span
                style={{
                  paddingRight: 10,
                  display:
                    activeTab === 'onsoundwise' && !isLoading && isEmpty(lastError)
                      ? 'inline-block'
                      : 'none',
                }}>
                {`Active listeners: ${(activeListeners || []).length}`}
              </span>
              <span
                style={{
                  paddingRight: 10,
                  display:
                    activeTab === 'cloudfrontstat' && !isLoading && isEmpty(lastError)
                      ? 'inline-block'
                      : 'none',
                }}>
                {` ${prettyTrafficValue(cfTotalTraffic)}`}
              </span>
              <span
                style={{
                  paddingRight: 10,
                  display:
                    activeTab === 'bandwidth' && !isLoading && isEmpty(lastError)
                      ? 'inline-block'
                      : 'none',
                }}>
                {` ${prettyTrafficValue(bandwidthTotalBytes)}`}
              </span>
              {!isEmpty(lastError) && (
                <span
                  style={{
                    paddingRight: 10,
                    color: Colors.mainRed,
                  }}>
                  {lastError}
                </span>
              )}
            </div>
          </div>

          {/* On Soundwise Tab Content */}
          <div
            style={{
              // display: activeTab === 'onsoundwise' ? 'block' : 'none',
              visibility: activeTab === 'onsoundwise' ? 'visible' : 'hidden',
              height: activeTab === 'onsoundwise' ? '' : 0,
              overflow: 'hidden',
            }}>
            <div className="row">
              <div className="col-md-12" style={styles.colWrapper}>
                {/* Loading overlay a chart */}
                <div style={{ ...styles.loaderWrapper, display: isLoading ? 'block' : 'none' }}>
                  <div style={styles.loaderInner}>
                    <Loading text={`Loading data ${loadingPercent}`} align="center" size={50} fontSize={2} />
                  </div>
                </div>

                <div style={styles.chartWrapper}>
                  <Bar data={data} width={100} height={450} options={barOptions(minutesToHms)} />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 col-lg-6" style={styles.colWrapper}>
                {/* Loading overlay a TOP TRACKS */}
                <div style={{ ...styles.loaderWrapper, display: isLoading || isLoadingTopTracks ? 'block' : 'none' }}>
                  <div style={styles.loaderInner}>
                    <Loading text={isLoading ? "Waiting for data" : "Loading data"} align="center" size={50} fontSize={2} />
                  </div>
                </div>

                <div style={styles.tableWrapper}>
                  <div style={styles.sectionTitleWrapper}>TOP TRACKS</div>
                  <div style={styles.tableDiv}>
                    <div style={{ paddingRight: 5 }}>
                      <table className="table">
                        <thead>
                          <tr style={styles.tr}>
                            <th style={{ ...styles.th }}>
                              {!this.isTotal(currentSoundcastID)
                                ? 'TITLE'
                                : 'TITLE [SOUNDCAST TITLE]'}
                            </th>
                            <th style={{ ...styles.th }}>PUBLISHED ON</th>
                            <th style={{ ...styles.th }}>TOTAL LISTENS</th>
                            <th style={{ ...styles.th }}>AVE. DURATION (min)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {episodeArr.map((episode, i) => {
                            const ave_duration = printableValue(episode.length / episode.listens / 60);
                            let episodeTitle = episode.title; 
                            if (this.isTotal(currentSoundcastID) && episode.title !== 'Other') {
                              episodeTitle += ' [' + episode.soundcastsTitle + ']'
                            }
                            return (
                              <tr key={i} style={styles.tr}>
                                <td style={{ ...styles.td }}>{episodeTitle}</td>
                                <td style={{ ...styles.td }}>{episode.date_created}</td>
                                <td style={{ ...styles.td }}>{episode.listens}</td>
                                <td style={{ ...styles.td }}>{ave_duration}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-12 col-lg-6" style={styles.colWrapper}>
                {/* Loading overlay a TOP LISTENERS */}
                <div style={{ ...styles.loaderWrapper, display: isLoading || isLoadingTopListeners ? 'block' : 'none' }}>
                  <div style={styles.loaderInner}>
                    <Loading text={isLoading ? "Waiting for data" : "Loading data"} align="center" size={50} fontSize={2} />
                  </div>
                </div>

                <div style={styles.tableWrapper}>
                  <div style={styles.sectionTitleWrapper}>
                    TOP LISTENERS
                    <span className="pull-right">
                      {csvData && csvData.length > 0 && (
                        <CSVLink
                          data={csvData}
                          style={{ color: Colors.mainOrange }}
                          target="_blank"
                          filename={`${currentSoundcast.title}-(Period: ${daysInterval})`}
                          headers={csvHeaders}>
                          Download listener stats
                        </CSVLink>
                      )}
                    </span>
                  </div>
                  <div style={styles.tableDiv}>
                    <div style={{ paddingRight: 5 }}>
                      <table className="table">
                        <thead>
                          <tr style={styles.tr}>
                            <th style={{ ...styles.th, width: '10%' }} />
                            <th style={{ ...styles.th, width: '40%' }}>NAME</th>
                            <th style={{ ...styles.th, width: '25%' }}># OF LISTENS</th>
                            <th style={{ ...styles.th, width: '25%' }}>TOTAL MINUTES</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userArr.map((user, i) => {
                            const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'noname';
                            const backgroundImage = `url(${user.pic_url})`;
                            const length = printableValue(user.length / 60);
                            return (
                              <tr key={i} style={styles.tr}>
                                <td style={{ ...styles.td, width: '10%' }}>
                                  <div style={{ ...styles.userImage, backgroundImage }} />
                                </td>
                                <td style={{ ...styles.td, width: '40%' }}>{name}</td>
                                <td style={{ ...styles.td, width: '25%' }}>{user.listens}</td>
                                <td style={{ ...styles.td, width: '25%' }}>{length}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Overall Tab Content */}
          <div
            style={{
              // display: activeTab === 'overall' ? 'block' : 'none',
              visibility: activeTab === 'overall' ? 'visible' : 'hidden',
              height: activeTab === 'overall' ? '' : 0,
              overflow: 'hidden',
            }}>
            <div className="row">
              <div className="col-md-12" style={styles.colWrapper}>
                {/* Loading overlay */}
                <div style={{ ...styles.loaderWrapper, display: isLoading ? 'block' : 'none' }}>
                  <div style={styles.loaderInner}>
                    <Loading text="Loading data" align="center" size={50} fontSize={2} />
                  </div>
                </div>

                <div style={styles.chartWrapper}>
                  <Bar data={overallData} width={100} height={450} options={barOptions()} />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 col-lg-6" style={{ margin: 0, padding: 0 }}>
                <div className="col-md-12" style={styles.colWrapper}>
                  <div style={styles.tableWrapper}>
                    <div style={styles.sectionTitleWrapper}>TOP TRACKS</div>
                    <div style={styles.tableDiv}>
                      <div style={{ paddingRight: 5 }}>
                        <table className="table">
                          <thead>
                            <tr style={styles.tr}>
                              <th style={{ ...styles.th, width: '62%' }}>TITLE</th>
                              <th style={{ ...styles.th, width: '25%' }}>PUBLISHED ON</th>
                              <th style={{ ...styles.th, width: '13%' }}>DOWNLOADS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {overallEpisodes.map((item, i) => {
                              return (
                                <tr key={i} style={styles.tr}>
                                  <td style={{ ...styles.td, width: '62%' }}>{item.title}</td>
                                  <td style={{ ...styles.td, width: '25%' }}>{item.published}</td>
                                  <td style={{ ...styles.td, width: '13%' }}>{item.downloads}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-12" style={styles.colWrapper}>
                  <div style={{ ...styles.tableWrapper }}>
                    <div style={styles.sectionTitleWrapper}>TOP COUNTRIES</div>
                    <table className="table">
                      <thead>
                        <tr style={styles.tr}>
                          <th style={{ ...styles.th, width: '90%' }}>COUNTRY</th>
                          <th style={{ ...styles.th, width: '10%' }}>DOWNLOADS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {countries.map((item, i) => {
                          return (
                            <tr key={i} style={styles.tr}>
                              <td style={{ ...styles.td, width: '90%' }}>{item.label}</td>
                              <td style={{ ...styles.td, width: '10%' }}>{item.downloads}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="col-md-12" style={styles.colWrapper}>
                  <div style={styles.tableWrapper}>
                    <div style={styles.sectionTitleWrapper}>TOP CITIES</div>
                    <table className="table">
                      <thead>
                        <tr style={styles.tr}>
                          <th style={{ ...styles.th, width: '55%' }}>CITY</th>
                          <th style={{ ...styles.th, width: '38%' }}>COUNTRY</th>
                          <th style={{ ...styles.th, width: '7%' }}>DOWNLOADS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cities.map((item, i) => {
                          return (
                            <tr key={i} style={styles.tr}>
                              <td style={{ ...styles.td, width: '55%' }}>{item.label}</td>
                              <td style={{ ...styles.td, width: '38%' }}>{item.country}</td>
                              <td style={{ ...styles.td, width: '7%' }}>{item.downloads}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="col-md-12 col-lg-6" style={styles.colWrapper}>
                <div style={styles.tableWrapper}>
                  <div style={styles.sectionTitleWrapper}>TOP BROWSERS</div>
                  <table className="table">
                    <thead>
                      <tr style={styles.tr}>
                        <th style={{ ...styles.th, width: '90%' }}>BROWSER</th>
                        <th style={{ ...styles.th, width: '10%' }}>DOWNLOADS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {browsers.map((item, i) => {
                        return (
                          <tr key={i} style={styles.tr}>
                            <td style={{ ...styles.td, width: '90%' }}>{item.label}</td>
                            <td style={{ ...styles.td, width: '10%' }}>{item.downloads}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="col-md-12 col-lg-6" style={styles.colWrapper}>
                <div style={styles.tableWrapper}>
                  <div style={styles.sectionTitleWrapper}>TOP USER AGENTS</div>
                  <table className="table">
                    <thead>
                      <tr style={styles.tr}>
                        <th style={{ ...styles.th, width: '90%' }}>USER AGENT</th>
                        <th style={{ ...styles.th, width: '10%' }}>DOWNLOADS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map((item, i) => {
                        return (
                          <tr key={i} style={styles.tr}>
                            <td style={{ ...styles.td, width: '90%' }}>{item.label}</td>
                            <td style={{ ...styles.td, width: '10%' }}>{item.downloads}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* On Cloudfront Tab Content */}
          <div
            style={{
              // display: activeTab === 'cloudfrontstat' ? 'block' : 'none',
              visibility: activeTab === 'cloudfrontstat' ? 'visible' : 'hidden',
              height: activeTab === 'cloudfrontstat' ? '' : 0,
              overflow: 'hidden',
            }}>
            <div className="row">
              <div className="col-md-12" style={styles.colWrapper}>
                {/* Loading overlay a chart */}
                <div style={{ ...styles.loaderWrapper, display: isLoading ? 'block' : 'none' }}>
                  <div style={styles.loaderInner}>
                    <Loading text="Loading data" align="center" size={50} fontSize={2} />
                  </div>
                </div>

                <div style={styles.chartWrapper}>
                  <Bar data={cfData} width={100} height={450}
                    options={barOptions(prettyTrafficValue, { yMax: 1, yTitle: cfDataUnit })} />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 col-lg-12" style={styles.colWrapper}>
                {/* Loading cloudfront a TOP TRACKS */}
                <div style={{ ...styles.loaderWrapper, display: isLoading ? 'block' : 'none' }}>
                  <div style={styles.loaderInner}>
                    <Loading text="Loading data" align="center" size={50} fontSize={2} />
                  </div>
                </div>

                <div style={styles.tableWrapper}>
                  <div style={styles.sectionTitleWrapper}>TOP TRACKS</div>
                  <div style={styles.tableDiv}>
                    <div style={{ paddingRight: 5 }}>
                      <table className="table">
                        <thead>
                          <tr style={styles.tr}>
                            <th style={{ ...styles.th }}>
                              {!this.isTotal(currentSoundcastID)
                                ? 'TITLE'
                                : 'TITLE [SOUNDCAST TITLE]'}
                            </th>
                            <th style={{ ...styles.th }}>PUBLISHED ON</th>
                            <th style={{ ...styles.th }}>TRAFFIC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cfEpisodes.map((episode, i) => {
                            return (
                              <tr key={i} style={styles.tr}>
                                <td style={{ ...styles.td }}>{episode.title}</td>
                                <td style={{ ...styles.td }}>{episode.publishedAt}</td>
                                <td style={{ ...styles.td }}>{prettyTrafficValue(episode.bytes)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* On Total Bandwidth Tab Content */}
          <div
            style={{
              // display: activeTab === 'bandwidth' ? 'block' : 'none',
              visibility: activeTab === 'bandwidth' ? 'visible' : 'hidden',
              height: activeTab === 'bandwidth' ? '' : 0,
              overflow: 'hidden',
            }}>
            <div className="row">
              <div className="col-md-12" style={styles.colWrapper}>
                {/* Loading overlay a chart */}
                <div style={{ ...styles.loaderWrapper, display: isLoading ? 'block' : 'none' }}>
                  <div style={styles.loaderInner}>
                    <Loading text="Loading data" align="center" size={50} fontSize={2} />
                  </div>
                </div>

                <div style={styles.chartWrapper}>
                  <Bar data={bandwidthData} width={100} height={450}
                    options={barOptions(prettyTrafficValue, { yMax: 1, yTitle: bandwidthDataUnit })} />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 col-lg-6" style={styles.colWrapper}>
                {/* Loading monthly data table */}
                <div style={{ ...styles.loaderWrapper, display: isLoading ? 'block' : 'none' }}>
                  <div style={styles.loaderInner}>
                    <Loading text="Loading data" align="center" size={50} fontSize={2} />
                  </div>
                </div>

                <div style={styles.tableWrapper}>
                  <div style={styles.sectionTitleWrapper}>TOTAL BANDWIDTH BY MONTHS</div>
                  <div style={styles.tableDiv}>
                    <div style={{ paddingRight: 5 }}>
                      <table className="table">
                        <thead>
                          <tr style={styles.tr}>
                            <th style={{ ...styles.th }}>MONTH</th>
                            <th style={{ ...styles.th }}>TOTAL TRAFFIC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bandwidthTable.map((item, i) => {
                            return (
                              <tr key={i} style={styles.tr}>
                                <td style={{ ...styles.td }}>{item.label}</td>
                                <td style={{ ...styles.td }}>{prettyTrafficValue(item.bytes)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>


        </div>
      </div>
    );
  }
}

const gutterSpacing = 6; // bootstrap gutter, default 15

const styles = {
  colWrapper: {
    marginTop: gutterSpacing,
    marginBottom: gutterSpacing,
  },
  soundcastSelectWrapper: {
    height: 40,
    maxWidth: 'calc(100% - 250px)',
    marginTop: 0,
    marginLeft: 30,
    marginRight: 30,
    display: 'inline-block',
  },
  soundcastSelect: {
    backgroundColor: Colors.mainWhite,
    width: 'calc(100% - 20px)',
    height: 40,
    marginLeft: 10,
    marginRight: 10,
    marginTop: 0,
    fontSize: 16,
  },
  intervalSelect: {
    width: 280,
    height: 34,
    fontSize: 14,
    marginBottom: 0,
    marginLeft: 0,
    padding: `4px 15px`,
  },
  sectionTitleWrapper: {
    marginBottom: 10,
    paddingBottom: 5,
    fontSize: 16,
    fontWeight: 600,
    borderBottomWidth: 1,
    borderBottomColor: Colors.darkBorder,
    borderBottomStyle: 'solid',
  },
  option: {
    fontSize: 16,
  },
  chartWrapper: {
    padding: 20,
    backgroundColor: Colors.mainWhite,
  },
  tableWrapper: {
    padding: 20,
    backgroundColor: Colors.mainWhite,
  },
  tableDiv: {
    overflowX: `hidden`,
    overflowY: `auto`,
    maxHeight: 500,
    marginRight: -15,
  },
  selectWrapper: {
    backgroundColor: Colors.mainWhite,
    fontSize: 14,
    padding: `6px 0 6px 17px`,
  },
  tr: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.lightBorder,
    borderBottomStyle: 'solid',
  },
  th: {
    fontSize: 14,
    color: Colors.fontGrey,
    height: 35,
    fontWeight: 'regular',
    verticalAlign: 'middle',
    wordWrap: 'break-word',
  },
  td: {
    color: Colors.softBlack,
    fontSize: 14,
    height: 45,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // whiteSpace: 'nowrap',
    wordWrap: 'break-word',
    paddingTop: 13,
  },
  userImage: {
    width: 26,
    height: 26,
    float: 'left',
    marginLeft: 10,
    marginRight: 5,
    marginTop: -4,
    borderRadius: '50%',
    backgroundColor: Colors.mainWhite,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: Colors.lightGrey,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'cover',
  },
  loaderWrapper: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 9999,
    background: 'rgba(255, 255, 255, 0.8)',
  },
  loaderInner: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};
